import { getAuthHeaders } from "../shared";

// Canonical currency code for the whole monorepo. clientapi is the lowest-level
// package every other one depends on, so this is the single source of truth —
// to add a currency, add it here and the derived types/lists pick it up.
export type Currency =
  | "USD"
  | "GBP"
  | "EUR"
  | "INR"
  | "IDR"
  | "MXN"
  | "BRL"
  | "CAD"
  | "AUD"
  | "PHP"
  | "AED"
  | "SAR"
  | "PLN"
  | "THB"
  | "MYR"
  | "JPY"
  | "KRW"
  | "CHF"
  | "ILS"
  | "SEK"
  | "SGD"
  | "NZD"
  | "CNY"
  | "TRY"
  | "ARS"
  | "PEN"
  | "CLP"
  | "COP"
  | "RON"
  | "VND"
  | "PKR"
  | "UAH"
  | "EGP"
  | "BOB"
  | "BDT";

export interface Coupon {
  amountOff?: number;
  percentOff?: number;
  id?: string;
  metadata?: {
    active?: string;
  };
}

export interface AvailableUpgrade {
  id: string;
  type: "creator" | "power" | "basic";
  title: string;
  catalogue?: "new_subscription";
  description?: Array<{
    title: string;
    color: string;
  }>;
  amount: number;
  mostPopular: boolean;
  percentageDiscount?: number;
  discountedAmount?: number;
  currency?: Currency;
  conversions: string;
  models: string | number;
  product: {
    id: string;
    name: string;
    coupons?: Coupon[];
  };
  plan: { interval: "month" | "year" };
  selectedCoupon?: Coupon;
}

export interface UpgradeOptionsResponse {
  subscription?: {
    currency: Currency;
  };
  availableUpgrades: AvailableUpgrade[];
  /** Whether the first-month promo is actually applied when this user pays. */
  promoApplies?: boolean;
}

// @TODO should come from backend as metadata
// but for now centralising here in a mutator
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mutateResponse = (data: any) => {
  return {
    subscription: data.subscription,
    // Forwarded verbatim: this object is rebuilt field-by-field, so anything
    // not listed here is silently dropped before it reaches the UI.
    promoApplies: data.promoApplies,
    availableUpgrades: data.availableUpgrades
      .map((upgrade: AvailableUpgrade) => {
        const { coupons } = upgrade.product;

        console.log(upgrade.product);

        const selectedCoupon =
          coupons?.find((coupon) => coupon.metadata?.active === "true") ||
          coupons?.[0];

        return {
          ...upgrade,
          selectedCoupon,
          mostPopular: upgrade.product.name === "Jammable Creator",
          type: {
            "Jammable Basic": "basic",
            "Jammable Creator": "creator",
            "Jammable Power User": "power",
          }[upgrade.product.name],
          title: {
            "Jammable Basic": "Starter",
            "Jammable Creator": "Creator",
            "Jammable Power User": "Power",
          }[upgrade.product.name],
          description: {
            "Jammable Basic": [
              {
                title: "Unlimited Covers",
                color: "tw-bg-cyan-200",
              },
              {
                title: "Duets",
                color: "tw-bg-pink-300",
              },
              {
                title: "Text to Speech",
                color: "tw-bg-red-300",
              },
              {
                title: "Full Voice Library",
                color: "tw-bg-green-200",
              },
            ],
            "Jammable Creator": [
              {
                title: "Unlimited Covers",
                color: "tw-bg-cyan-200",
              },
              {
                title: "Train Unlimited Voices",
                color: "tw-bg-yellow-100",
              },
              {
                title: "Text to Speech",
                color: "tw-bg-green-200",
              },
              {
                title: "Premium Quality",
                color: "tw-bg-blue-200",
              },
              {
                title: "Create AI Videos",
                color: "tw-bg-pink-200",
              },
            ],
            "Jammable Power User": [
              {
                title: "Unlimited Covers",
                color: "tw-bg-purple-100",
              },
              {
                title: "10 Monthly Voices",
                color: "tw-bg-yellow-100",
              },
            ],
          }[upgrade.product.name],
          models: {
            "Jammable Creator": 5,
            "Jammable Power User": 10,
          }[upgrade.product.name],
          conversions: {
            "Jammable Basic": "25 per month",
            "Jammable Creator": "Unlimited",
            "Jammable Power User": "Unlimited",
          }[upgrade.product.name],
          ...(selectedCoupon?.amountOff
            ? {
                discountedAmount: upgrade.amount - selectedCoupon.amountOff,
                percentageDiscount: Math.round(
                  (selectedCoupon.amountOff * 100) / upgrade.amount,
                ),
              }
            : {}),
        };
      })
      .sort((a: AvailableUpgrade, b: AvailableUpgrade) => {
        // Calculate the final price considering discountedAmount if present
        const finalPriceA = a.discountedAmount ?? a.amount;
        const finalPriceB = b.discountedAmount ?? b.amount;
        return finalPriceA - finalPriceB;
      }),
  };
};

export async function fetchSubscriptionUpgradeOptions(
  interval = "month",
  catalogue = "new_subscription",
): Promise<UpgradeOptionsResponse> {
  const response = await fetch(`/api/payment/subscription`, {
    headers: { ...(await getAuthHeaders()) },
  });
  const data = await response.json();
  const mutated = mutateResponse(data);
  return {
    ...mutated,
    availableUpgrades: mutated.availableUpgrades.filter(
      (upgrade: AvailableUpgrade) => {
        return (
          upgrade.plan.interval === interval && upgrade.catalogue === catalogue
        );
      },
    ),
  };
}
